import { WorkflowStateEvent } from '@/types/workflow-state-events'

/**
 * The workflow state events pixydocs-api owns a code for. Their names follow
 * the rows api migration 0237 created, and `description` is the "when it
 * fires" line the picker shows beneath each name.
 */
export const SYSTEM_EVENTS = [
  {
    code: 'on_enter',
    name: 'On Enter',
    description: 'Fires when a document enters this state.',
    requiresApprovalsFlag: false,
  },
  {
    code: 'on_exit',
    name: 'On Exit',
    description: 'Fires when a document leaves this state.',
    requiresApprovalsFlag: false,
  },
  {
    code: 'approvals_met',
    name: 'Approvals Met',
    description:
      'Fires when every approval policy on the document has been satisfied. This only applies if there are approval policies applied to the document.',
    requiresApprovalsFlag: true,
  },
  {
    code: 'approvals_rejected',
    name: 'Approval Rejected',
    description:
      'Fires when an approver rejects the document. This only applies if there are approval policies applied to the document.',
    requiresApprovalsFlag: true,
  },
] as const

export type SystemEvent = typeof SYSTEM_EVENTS[number]
export type SystemEventCode = SystemEvent['code']

export const SYSTEM_EVENT_CODES = SYSTEM_EVENTS.map(({ code }) => code)

/**
 * Typed so it stops compiling if `on_enter` ever leaves SYSTEM_EVENTS. Callers
 * that single one out are reasoning about the initial state's upload gate.
 */
export const ON_ENTER_EVENT_CODE: SystemEventCode = 'on_enter'

/** The value the api's `code__in` / `code__in!` event filters take. */
export const SYSTEM_EVENT_CODES_FILTER = SYSTEM_EVENT_CODES.join(',')

export type SystemEventOption = SystemEvent & { isAdded: boolean }

/**
 * Every system event the state could hold, including the ones it already has.
 * The picker shows those too, disabled, so the full set stays visible.
 */
export function getSystemEventOptions(
  existingCodes: string[],
  showApprovals: boolean,
): SystemEventOption[] {
  return SYSTEM_EVENTS.filter(
    (event) => !event.requiresApprovalsFlag || showApprovals,
  ).map((event) => ({ ...event, isAdded: existingCodes.includes(event.code) }))
}

const SYSTEM_EVENT_SORT_ORDER = new Map<string, number>(
  SYSTEM_EVENTS.map(({ code }, index) => [code, index]),
)

function systemEventRank(code: string) {
  return SYSTEM_EVENT_SORT_ORDER.get(code) ?? SYSTEM_EVENTS.length
}

/**
 * Puts events in SYSTEM_EVENTS order so every list of them reads the same top
 * to bottom — the picker, the state's lanes, and the run menus. Codes we
 * don't own sort last and keep the order the api gave them.
 */
export function sortSystemEvents<T extends { code: string }>(events: T[]): T[] {
  return events.toSorted(
    (a, b) => systemEventRank(a.code) - systemEventRank(b.code),
  )
}

export function getSystemEvent(code: string): SystemEvent | undefined {
  return SYSTEM_EVENTS.find((event) => event.code === code)
}

/**
 * Splits a state's events into the two lanes the settings page shows. The
 * approval events drop out entirely without the approvals flag, matching the
 * picker — a state configured with them elsewhere keeps them, just unlisted.
 */
export function getWorkflowStateEventGroups(
  workflowStateEvents: WorkflowStateEvent[],
  showApprovals: boolean,
): {
  systemEvents: WorkflowStateEvent[]
  customEvents: WorkflowStateEvent[]
} {
  const { systemEvents, customEvents } = workflowStateEvents.reduce<{
    systemEvents: WorkflowStateEvent[]
    customEvents: WorkflowStateEvent[]
  }>(
    (acc, event) => {
      const systemEvent = getSystemEvent(event.code)
      if (!systemEvent) {
        acc.customEvents.push(event)
      } else if (!systemEvent.requiresApprovalsFlag || showApprovals) {
        acc.systemEvents.push(event)
      }
      return acc
    },
    { systemEvents: [], customEvents: [] },
  )

  return { systemEvents: sortSystemEvents(systemEvents), customEvents }
}
